import React, { createContext, useContext, useMemo } from 'react';
import axios, { AxiosInstance } from 'axios';
import { initializeService } from '../services/InitializeService';
import { PublicClientApplication } from '@azure/msal-browser';
import { config } from '../services/Constants';
import { docMgmtInstance, initializeDocMgmtService } from '../services/InitializeDocMgmtService';
import { calendarClientInstance, initializeCalendarService } from '../services/InitializeCalendarService';
import { myProviderDataInstance, initializeMyProviderDataService } from '../services/InitializeMyProviderDataService';
import { pbsLiveClientInstance, initializePbsLiveService } from '../services/InitializePbsLiveService';

// Define the ApiContext type
interface ApiContextType {
  apiClient: AxiosInstance;
  docMgmtClient: AxiosInstance;
  calendarClient: AxiosInstance;
  myProviderDataClient: AxiosInstance;
  pbsLiveClient: AxiosInstance;
}

// Create the ApiContext
const ApiContext = createContext<ApiContextType | null>(null);

// Define the provider props
interface ApiProviderProps {
  msalApp: PublicClientApplication;
  children: React.ReactNode;
}
const apiBaseURL = config.url.API_URL;

// ApiProvider component
const ApiProvider: React.FC<ApiProviderProps> = ({ msalApp, children }) => {
  const contextValue = useMemo(() => {
    const instance = axios.create({
      baseURL: apiBaseURL,
      timeout: 120000,
    });
    initializeService(msalApp, instance);
    
    // Initialize docMgmt and calendar clients
    initializeDocMgmtService(msalApp);
    initializeCalendarService(msalApp);
    initializeMyProviderDataService(msalApp);
    initializePbsLiveService(msalApp);
    return {
      apiClient: instance,
      docMgmtClient: docMgmtInstance,
      calendarClient: calendarClientInstance,
      myProviderDataClient: myProviderDataInstance,
      pbsLiveClient: pbsLiveClientInstance,
    };
  }, [msalApp]);

  return <ApiContext.Provider value={contextValue}>{children}</ApiContext.Provider>;
};

// Custom hook to use Axios instance
const useApiClient = (): AxiosInstance => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context.apiClient;
};

// Custom hook to use DocMgmt client
const useDocMgmtClient = (): AxiosInstance => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useDocMgmtClient must be used within an ApiProvider');
  }
  return context.docMgmtClient;
};

// Custom hook to use Calendar client
const useCalendarClient = (): AxiosInstance => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useCalendarClient must be used within an ApiProvider');
  }
  return context.calendarClient;
};

const useMyProviderDataClient = (): AxiosInstance => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useMyProviderDataClient must be used within an ApiProvider');
  }
  return context.myProviderDataClient;
};

const usePbsLiveClient = (): AxiosInstance => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('usePbsLiveClient must be used within an ApiProvider');
  }
  return context.pbsLiveClient;
};

export { ApiProvider, useApiClient, useDocMgmtClient, useCalendarClient, useMyProviderDataClient, usePbsLiveClient };